import React, { useState, useRef } from 'react';
import { B } from '../constants';
import { promos as promosAPI, reelEnhancement } from '../api';
import Modal from './ui/Modal';
import Input from './ui/Input';
import VideoTrimmer from './VideoTrimmer';
import DurationAnalyzer from './DurationAnalyzer';
import AspectRatioSelector from './AspectRatioSelector';
import QualitySettings from './QualitySettings';
import BatchUploadModal from './BatchUploadModal';
import TrendingSounds from './TrendingSounds';
import { videoUrlRejectionReason } from '../lib/videoUrl';

const EXPIRY_OPTIONS = [
  { label: '24 Hours', days: 1, icon: '⚡', desc: 'Flash promo — urgent & exclusive' },
  { label: '3 Days',   days: 3, icon: '🔥', desc: 'Sweet spot for most promos' },
  { label: '7 Days',   days: 7, icon: '📅', desc: 'Full week of visibility' },
  { label: '2 Weeks',  days: 14, icon: '🌙', desc: 'Extended campaign' },
  { label: '30 Days',  days: 30, icon: '🗓️', desc: 'Long-run promotion' },
];

const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
const MAX_SIZE = 100 * 1024 * 1024;

export default function PromoUploadModal({ onClose, onSuccess, user, onAuthRequired, initialSound, onBatchCreated }) {
  const [title,      setTitle]      = useState('');
  const [videoUrl,   setVideoUrl]   = useState('');
  const [thumbUrl,   setThumbUrl]   = useState('');
  const [caption,    setCaption]    = useState('');
  const [destination,setDest]       = useState('');
  const [expiryDays, setExpiryDays] = useState(3);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [step,       setStep]       = useState(1);
  const [inputMode,  setInputMode]  = useState('url');
  const [file,       setFile]       = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading,  setUploading]  = useState(false);
  const [dragOver,   setDragOver]   = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimParams, setTrimParams] = useState(null);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [cropMode, setCropMode] = useState('letterbox');
  const [targetAspectRatio, setTargetAspectRatio] = useState('9:16');
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [qualitySettings, setQualitySettings] = useState({ preset: 'medium', codec: 'h264', customBitrate: null });
  const [selectedSound, setSelectedSound] = useState(initialSound || null);
  const [showSoundPicker, setShowSoundPicker] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError(`Invalid file type. Allowed: MP4, MOV, WebM, AVI`);
      return;
    }
    if (selectedFile.size > MAX_SIZE) {
      setError(`File too large. Maximum size is ${MAX_SIZE / 1024 / 1024}MB`);
      return;
    }
    setFile(selectedFile);
    setError('');
    const url = URL.createObjectURL(selectedFile);
    setFilePreview(url);
    if (!title) setTitle(selectedFile.name.replace(/\.[^/.]+$/, '').slice(0, 80));

    // Use a SEPARATE object URL for one-shot metadata extraction. Revoking
    // here would kill the URL bound to filePreview (and any downstream
    // <video> element, including VideoTrimmer), breaking playback seconds
    // after file select.
    const metaVideo = document.createElement('video');
    const metaUrl = URL.createObjectURL(selectedFile);
    metaVideo.preload = 'metadata';
    metaVideo.onloadedmetadata = () => {
      const dur = Math.round(metaVideo.duration);
      setVideoDuration(dur);
      // Leave trimParams null until the user actually trims. The backend
      // caps trimDuration at 140s; auto-defaulting to the full duration
      // would 400 every upload of a long video on Publish.
      setTrimParams(null);
      if (metaVideo.videoWidth && metaVideo.videoHeight) {
        const w = metaVideo.videoWidth;
        const h = metaVideo.videoHeight;
        const gcdVal = (a, b) => b === 0 ? a : gcdVal(b, a % b);
        const g = gcdVal(w, h);
        setVideoAspectRatio(`${w / g}:${h / g}`);
      }
      URL.revokeObjectURL(metaUrl);
    };
    metaVideo.onerror = () => URL.revokeObjectURL(metaUrl);
    metaVideo.src = metaUrl;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const uploadToS3 = async () => {
    if (!file) return null;
    setUploading(true);
    setUploadProgress(0);
    try {
      const { uploadUrl, key } = await promosAPI.getUploadUrl(file.type, file.size);
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          setUploading(false);
          setUploadProgress(100);
          if (xhr.status >= 200 && xhr.status < 300) resolve(key);
          else reject(new Error(`Upload failed (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => { setUploading(false); reject(new Error('Upload failed')); };
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    } catch (err) {
      setUploading(false);
      throw err;
    }
  };

  const handleSubmit = async () => {
    if (!user) { onAuthRequired(); return; }
    if (!title.trim()) { setError('Please add a title'); return; }

    if (inputMode === 'file' && !file && !videoUrl.trim()) {
      setError('Please select a video file or switch to URL mode');
      return;
    }
    if (inputMode === 'url' && !videoUrl.trim()) {
      setError('Please add a video URL');
      return;
    }

    // Normalize URLs before submit: auto-prepend https:// when the user
    // pasted "www.youtube.com/..." style links, and reject anything still
    // un-parseable so the backend schema doesn't 400 the whole publish.
    const rawThumb = thumbUrl.trim();
    let thumbNormalized = '';
    if (rawThumb) {
      const candidate = /^https?:\/\//i.test(rawThumb) ? rawThumb : `https://${rawThumb}`;
      try { new URL(candidate); thumbNormalized = candidate; }
      catch {
        setError('Thumbnail must be a valid image URL (e.g. https://...), or leave it empty');
        return;
      }
    }
    let videoUrlNormalized = videoUrl.trim();
    if (inputMode === 'url') {
      const candidate = /^https?:\/\//i.test(videoUrlNormalized) ? videoUrlNormalized : `https://${videoUrlNormalized}`;
      try { new URL(candidate); } catch {
        setError('Please enter a valid video URL (e.g. https://youtu.be/...)');
        return;
      }
      const rejection = videoUrlRejectionReason(candidate);
      if (rejection) {
        setError(rejection);
        return;
      }
      videoUrlNormalized = candidate;
      setVideoUrl(candidate);
    }

    setLoading(true);
    setError('');
    try {
      let videoKey = null;
      let videoSource = 'external';
      let finalVideoUrl = videoUrlNormalized;

      if (inputMode === 'file' && file) {
        videoKey = await uploadToS3();
        videoSource = 's3';
        finalVideoUrl = null;
      }

      const createPayload = {
        title,
        videoUrl: finalVideoUrl || undefined,
        videoKey: videoKey || undefined,
        videoSource,
        thumbnailUrl: thumbNormalized || null,
        caption: caption || null,
        destination: destination || null,
        expireAfterDays: expiryDays,
        cropMode,
      };
      // Clamp to the backend cap (140s) so an out-of-range trim from
      // VideoTrimmer (e.g. Reset on a long video) doesn't 400 the create.
      if (trimParams && trimParams.trimDuration > 0) {
        createPayload.trimStart = Math.max(0, trimParams.trimStart || 0);
        createPayload.trimDuration = Math.min(140, trimParams.trimDuration);
      }
      const promo = await promosAPI.create(createPayload);

      if (selectedSound) {
        try {
          await reelEnhancement.saveMusic(promo.id, {
            trackId: String(selectedSound.id),
            trackName: selectedSound.name,
            artist: selectedSound.artist || null,
            mood: selectedSound.mood || 'upbeat',
            url: selectedSound.url || '',
            durationSecs: null,
          });
        } catch (e) {
          console.error('Failed to save background music:', e);
        }
      }

      onSuccess({ ...promo, selectedSound });
      setSelectedSound(null);
    } catch (err) {
      setError(err.message || 'Failed to post promo');
    } finally { setLoading(false); }
  };

  const removeFile = () => {
    setFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setUploadProgress(0);
    fileInputRef.current.value = '';
  };

  // Render batch upload on its own instead of nesting a fixed overlay inside the
  // animated Modal card, where it was clipped to the card's box.
  if (showBatch) {
    return (
      <BatchUploadModal
        onClose={() => setShowBatch(false)}
        onSuccess={(batch) => { setShowBatch(false); onBatchCreated?.(batch); onClose(); }}
        user={user}
        onAuthRequired={onAuthRequired}
      />
    );
  }

  return (
    <Modal isOpen onClose={onClose} size="md" showCloseButton={false} ariaLabel="Post Creator Promo">

        <div style={{ background: B.night, padding: '24px 28px', borderRadius: '24px 24px 0 0', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${B.forest},${B.mint},${B.amber})` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, color: 'white', fontWeight: 700 }}>
                {user?.isInfluencer ? '✨ Post Creator Promo' : '🎬 Share Your Travel Story'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>
                {user?.isInfluencer ? 'Creator badge · Premium placement' : 'Inspire fellow travellers'}
              </p>
            </div>
            <span onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</span>
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 16 }}>
            {[1, 2, 3].map(s => (
              <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: step >= s ? B.mint : 'rgba(255,255,255,0.2)', transition: 'background 0.3s' }} />
            ))}
          </div>
        </div>

        <div style={{ padding: '28px' }}>
          {error && (
            <div style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#dc3545', fontSize: 13, fontFamily: "'Montserrat',sans-serif" }}>
              ⚠️ {error}
            </div>
          )}

          {step === 1 ? (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <button onClick={() => { setInputMode('file'); setError(''); }}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, background: inputMode === 'file' ? B.forest : '#f0f0f0', color: inputMode === 'file' ? 'white' : B.night }}>
                  📁 Upload Video
                </button>
                <button onClick={() => { setInputMode('url'); setError(''); }}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, background: inputMode === 'url' ? B.forest : '#f0f0f0', color: inputMode === 'url' ? 'white' : B.night }}>
                  🔗 Video URL
                </button>
              </div>

              {inputMode === 'url' ? (
                <>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      Video URL <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <Input type="url" placeholder="Paste a YouTube link or a direct video (.mp4) / Cloudinary URL"
                      value={videoUrl} onChange={e => setVideoUrl(e.target.value)} />
                    <p style={{ color: '#999', fontSize: 11, fontFamily: "'Montserrat',sans-serif", marginTop: 6 }}>
                      💡 Supported: YouTube links, direct video files (.mp4/.webm/.mov) and Cloudinary URLs.
                    </p>
                    <p style={{ color: '#999', fontSize: 11, fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>
                      🚫 Instagram and Facebook links can't be embedded — download the video and use 📁 Upload Video instead.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 20 }}
                    onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                    <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      Upload Video <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    {filePreview ? (
                      <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid #e5e0d8', position: 'relative' }}>
                        <video src={filePreview} style={{ width: '100%', maxHeight: 300, objectFit: 'contain', display: 'block' }} controls />
                        <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9f9f9', borderTop: '1px solid #e5e0d8' }}>
                          <span style={{ fontSize: 12, fontFamily: "'Montserrat',sans-serif", color: B.muted }}>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(1)}MB)
                          </span>
                          <button onClick={removeFile} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Remove</button>
                        </div>
                        {uploading && (
                          <div style={{ padding: '0 14px 10px' }}>
                            <div style={{ height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${uploadProgress}%`, background: B.forest, borderRadius: 3, transition: 'width 0.3s' }} />
                            </div>
                            <p style={{ fontSize: 11, color: B.muted, marginTop: 4, fontFamily: "'Montserrat',sans-serif" }}>Uploading... {uploadProgress}%</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                        style={{ border: `2px dashed ${dragOver ? B.forest : '#ddd'}`, borderRadius: 12, padding: '40px 20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? 'rgba(26,107,74,0.05)' : '#fafafa', transition: 'all 0.2s' }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
                        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 600, color: B.night, marginBottom: 6 }}>Drop video here or click to browse</p>
                        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: B.muted }}>MP4, MOV, WebM, AVI — max 100MB</p>
                      </div>
                    )}
                    <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                      onChange={e => handleFileSelect(e.target.files[0])}
                      style={{ display: 'none' }} />
                  </div>
                </>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Thumbnail Image URL (optional)
                </label>
                <Input type="url" placeholder="https://... (used as preview in Stories bar)"
                  value={thumbUrl} onChange={e => setThumbUrl(e.target.value)} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Title <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <Input type="text" placeholder="e.g. Hidden Gems in Bali 🌴"
                  value={title} onChange={e => setTitle(e.target.value)} maxLength={80} />
                <p style={{ color: '#bbb', fontSize: 11, textAlign: 'right', fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>{title.length}/80</p>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => {
                  if (!user) { onAuthRequired(); return; }
                  if (!title.trim()) { setError('Add a title first'); return; }
                  if (inputMode === 'url' && !videoUrl.trim()) { setError('Add a video URL first'); return; }
                  if (inputMode === 'url') {
                    const raw = videoUrl.trim();
                    const rejection = videoUrlRejectionReason(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
                    if (rejection) { setError(rejection); return; }
                  }
                  if (inputMode === 'file' && !file) { setError('Select a video file first'); return; }
                  setError('');
                  setStep(2);
                }} style={{ ...btnStyle, background: B.forest, color: 'white', flex: 2 }}>
                  Next → Set Details
                </button>
                <button onClick={() => setShowBatch(true)}
                  style={{ ...btnStyle, background: '#f0f0f0', color: B.night, flex: 1 }}>
                  📦 Multiple
                </button>
              </div>
            </>
          ) : step === 2 ? (
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Caption
                </label>
                <textarea placeholder="Tell the story behind this place..."
                  value={caption} onChange={e => setCaption(e.target.value)}
                  rows={3} maxLength={300}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #e5e0d8', fontFamily: "'Montserrat',sans-serif", fontSize: 14, resize: 'vertical', height: 'auto', boxSizing: 'border-box' }} />
                <p style={{ color: '#bbb', fontSize: 11, textAlign: 'right', fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>{caption.length}/300</p>
              </div>

              {videoDuration > 0 && (
                <DurationAnalyzer
                  duration={videoDuration}
                  onSuggestion={(suggestedDuration) => {
                    setTrimParams({ trimStart: 0, trimDuration: suggestedDuration });
                    setShowTrimmer(true);
                  }}
                />
              )}

              <AspectRatioSelector
                videoAspectRatio={videoAspectRatio || 'auto'}
                value={targetAspectRatio}
                cropMode={cropMode}
                onChangeRatio={setTargetAspectRatio}
                onChangeCropMode={setCropMode}
              />

              {file && videoDuration > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      ✂️ Trim Video
                    </label>
                    <button
                      onClick={() => setShowTrimmer(!showTrimmer)}
                      style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: showTrimmer ? '#e5e0d8' : B.forest, color: showTrimmer ? '#666' : 'white', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600 }}
                    >
                      {showTrimmer ? '✕ Skip Trimming' : '✂️ Trim'}
                    </button>
                  </div>
                  {showTrimmer && (
                    <VideoTrimmer
                      videoUrl={filePreview}
                      duration={videoDuration}
                      onChange={({ trimStart, trimDuration }) => setTrimParams({ trimStart, trimDuration })}
                      cropMode={cropMode}
                      targetAspectRatio={targetAspectRatio}
                      videoAspectRatio={videoAspectRatio}
                      platformMarkers={[
                        { label: 'YT Shorts', duration: 60, color: '#FF0000' },
                        { label: 'Instagram', duration: 90, color: '#E1306C' },
                        { label: 'Facebook', duration: 90, color: '#1877F2' },
                        { label: 'Twitter/X', duration: 140, color: '#1DA1F2' },
                      ]}
                    />
                  )}
                  {!showTrimmer && (
                    <p style={{ color: B.muted, fontSize: 11, fontFamily: "'Montserrat',sans-serif", background: '#f9f9f9', padding: '8px 12px', borderRadius: 8 }}>
                      Full video ({Math.floor(videoDuration / 60)}:{String(videoDuration % 60).padStart(2, '0')}) will be used
                    </p>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Destination location
                </label>
                <Input type="text" placeholder="e.g. Bali, Indonesia"
                  value={destination} onChange={e => setDest(e.target.value)} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    🎵 Background Music
                  </label>
                  <button
                    onClick={() => setShowSoundPicker(!showSoundPicker)}
                    style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: showSoundPicker ? '#e5e0d8' : B.forest, color: showSoundPicker ? '#666' : 'white', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600 }}
                  >
                    {showSoundPicker ? '✕ Done' : selectedSound ? '🎵 Change' : '🎵 Add Music'}
                  </button>
                </div>
                {showSoundPicker && (
                  <div style={{ background: '#1a2633', borderRadius: 12, border: '1px solid #2a3a48', padding: 12, marginBottom: 4 }}>
                    <TrendingSounds compact onSelect={(sound) => {
                      setSelectedSound(sound);
                      setShowSoundPicker(false);
                    }} />
                  </div>
                )}
                {selectedSound && !showSoundPicker && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(78,203,141,0.08)', border: '1px solid rgba(78,203,141,0.2)', borderRadius: 10 }}>
                    <div style={{ fontSize: 24 }}>🎵</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: B.night }}>{selectedSound.name}</div>
                      {selectedSound.artist && <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: '#666' }}>{selectedSound.artist}</div>}
                    </div>
                    <button onClick={() => setSelectedSound(null)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Remove</button>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  How long should this promo run?
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {EXPIRY_OPTIONS.map(opt => (
                    <div key={opt.days} onClick={() => setExpiryDays(opt.days)}
                      style={{ padding: '12px 14px', borderRadius: 12, border: `2px solid ${expiryDays === opt.days ? B.forest : '#e5e0d8'}`, background: expiryDays === opt.days ? 'rgba(26,107,74,0.06)' : 'white', cursor: 'pointer', transition: 'all 0.2s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 18 }}>{opt.icon}</span>
                        <span style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 13, color: expiryDays === opt.days ? B.forest : B.night }}>{opt.label}</span>
                      </div>
                      <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: '#999', marginLeft: 26 }}>{opt.desc}</p>
                    </div>
                  ))}
                </div>
                <p style={{ color: B.muted, fontSize: 12, fontFamily: "'Montserrat',sans-serif", marginTop: 10, background: 'rgba(26,107,74,0.06)', padding: '8px 12px', borderRadius: 8 }}>
                  ⏰ This promo will automatically disappear on <strong>{new Date(Date.now() + expiryDays * 86400000).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</strong>
                </p>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep(1)} style={{ ...btnStyle, background: '#f5f5f5', color: B.night, flex: 1 }}>← Back</button>
                <button onClick={() => setStep(3)} style={{ ...btnStyle, background: B.forest, color: 'white', flex: 2 }}>
                  Next → Quality
                </button>
              </div>
            </>
          ) : (
            <>
              <QualitySettings
                value={qualitySettings}
                onChange={setQualitySettings}
                durationSecs={videoDuration}
              />

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep(2)} style={{ ...btnStyle, background: '#f5f5f5', color: B.night, flex: 1 }}>← Back</button>
                <button onClick={handleSubmit} disabled={loading || uploading}
                  style={{ ...btnStyle, background: loading || uploading ? '#ccc' : B.forest, color: 'white', flex: 2, cursor: loading || uploading ? 'not-allowed' : 'pointer' }}>
                  {uploading ? `⏳ Uploading ${uploadProgress}%...` : loading ? '⏳ Posting...' : '🚀 Publish Promo'}
                </button>
              </div>
            </>
          )}
        </div>
    </Modal>
  );
}

const btnStyle   = { padding: '13px 24px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700, transition: 'all 0.2s' };
